import {
  Avatar,
  AvatarGroup,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Theme,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { Icons, TextLink } from 'utoniq-ui'

import {
  ConsolePatronDetailFragment,
  ConsoleProductDetailFragment,
  MembershipSubscriptionPlanStatus,
} from '@/clients/utoniq-core/schema'
import { MembershipCategory } from '@/components/organisms/membership/MembershipCategory'
import { useRouter, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

export type MembershipProductBoxProps = {
  product: ConsoleProductDetailFragment
  isDetail?: boolean
}

export const MembershipProductBox: Organism<MembershipProductBoxProps> = ({
  product,
  isDetail,
}) => {
  const { t } = useTranslation('consoleMembershipDetail')
  const { generatePath, routerPages } = useRouter()

  const memberships = product.plans.reduce<{
    members: { node: ConsolePatronDetailFragment }[]
    patronCount: number
  }>(
    (acc, test) => {
      acc.members = [...acc.members, ...(test.patrons?.edges || [])]
      acc.patronCount += test.patronCount || 0
      return acc
    },
    {
      members: [],
      patronCount: 0,
    }
  )

  const isPublish = useMemo(
    () =>
      product.plans.some(
        plan => plan.status === MembershipSubscriptionPlanStatus.Active
      ),
    [product.plans]
  )

  return (
    <OrganismFrame>
      <Card
        elevation={0}
        sx={{
          bgcolor: 'background.default',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'grey.100',
          borderRadius: 2,
        }}
      >
        <CardHeader
          title={product.title}
          titleTypographyProps={{ variant: 'h5' }}
          action={
            <MembershipCategory
              status={
                isPublish
                  ? MembershipSubscriptionPlanStatus.Active
                  : MembershipSubscriptionPlanStatus.Inactive
              }
            />
          }
          sx={{
            bgcolor: 'grey.50',
            borderBottom: ({ palette }: Theme) =>
              `1px solid ${palette?.grey?.['100']}`,
            '.MuiCardHeader-action': { m: 0 },
            px: 5,
            py: 3,
          }}
        />
        <CardContent sx={{ pt: 4, px: 5, pb: 5, '&:last-child': { pb: 5 } }}>
          <Stack spacing={2} sx={{ pb: 2 }}>
            <Typography variant="subtitle1">
              {product.plans.map(plan => t(plan.interval)).join('/')}
            </Typography>
            <Typography variant="body2">
              {product.plans
                .map(plan => t('circle', { number: plan.formattedAmount }))
                .join('/')}
            </Typography>
          </Stack>
          <Divider />
          <Stack sx={{ py: 3 }} spacing={2}>
            <Typography variant="subtitle1">{t('explanation')}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {product.content}
            </Typography>
          </Stack>
          {!!product.annualContent && (
            <Stack sx={{ pb: 3 }} spacing={2}>
              <Typography variant="subtitle1">
                {t('explanationAnnual')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {product.annualContent}
              </Typography>
            </Stack>
          )}
          {/* ユーザーのアバターと詳細へのリンク表示 */}
          {!isDetail && (
            <>
              <Divider />
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ pt: 5 }}
                spacing={2}
              >
                {memberships.members.length ? (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <AvatarGroup max={3} spacing="small">
                      {memberships.members
                        .splice(0, 3)
                        .map(({ node }, index) => (
                          <Avatar
                            key={index}
                            src={node.user.avatar?.smallUri ?? ''}
                            sx={{
                              width: 36,
                              height: 36,
                              zIndex: index,
                            }}
                          />
                        ))}
                    </AvatarGroup>
                    {!!memberships.patronCount && (
                      <Typography color="text.hint" variant="caption">
                        {t('patronCount', {
                          patronCount: memberships.patronCount,
                        })}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Stack>
                    <Typography variant="caption" color="text.hint">
                      {t('noMembership')}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" alignItems="center" spacing={2}>
                  <TextLink
                    color="primary"
                    href={generatePath(routerPages.ConsoleMembershipDetail, {
                      planId:
                        product.plans?.map(({ planId }) => planId)?.join(':') ??
                        '#',
                    })}
                    variant="subtitle1"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {t('detail')}
                  </TextLink>
                  <Icons.RightArrow color="primary" fontSize="xSmall" />
                </Stack>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </OrganismFrame>
  )
}
